import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PMDialog } from "../../../utils/PMDialog";
import { PMField } from "../../../utils/PMField";
import { Form, Formik } from "formik";
import { toast } from "react-hot-toast";
import * as Yup from "yup";
import axios from "../../../../api/axios";
import PMSwitch from "../../../utils/PMSwitch";
import { formatPrice, parsePrice, calculateExclVat, calculateInclVat } from "../../../../utils/formatPrice";
import { VatTypeSelectorButton } from "../../../utils/VatTypeSelector";
import ModernProjectActivitySelector from "../../../utils/Selector/ModernProjectActivitySelector";

function extractApiErrorMessages(data) {
  if (!data) return [];
  const err = data.errors;
  if (Array.isArray(err)) {
    return err.map((e) => (typeof e === "string" ? e : String(e))).filter(Boolean);
  }
  if (err && typeof err === "object") {
    return Object.entries(err).flatMap(([key, val]) => {
      const parts = Array.isArray(val) ? val : [val];
      return parts
        .filter((p) => p != null && String(p).length > 0)
        .map((p) => {
          const s = typeof p === "string" ? p : String(p);
          if (key === "base" || key === "Base") return s;
          return `${key}: ${s}`;
        });
    });
  }
  if (data.error != null) {
    const e = data.error;
    return [typeof e === "string" ? e : JSON.stringify(e)];
  }
  if (data.message != null) return [String(data.message)];
  return [];
}

/** Norsk standard MVA for avtalt pris når API ikke leverer MVA-type eller sats mangler. */
const DEFAULT_VAT_PERCENT_FALLBACK = 25;

function pickDefaultVatType(types) {
  if (!Array.isArray(types) || types.length === 0) return null;
  const by25 = types.find((vt) => Number(vt.percentage) === 25);
  if (by25) return by25;
  const by15 = types.find((vt) => Number(vt.percentage) === 15);
  if (by15) return by15;
  return types[0];
}

function getVatPercentForCalculation(selectedVatType) {
  if (selectedVatType != null) {
    const p = Number(selectedVatType.percentage);
    if (Number.isFinite(p)) return p;
  }
  return DEFAULT_VAT_PERCENT_FALLBACK;
}

export const ReportPriceLineDialog = ({ open, onClose, reportId, priceLine, project, onSuccess }) => {
  const { t } = useTranslation();
  const isEditing = !!priceLine;
  const [selectedVatType, setSelectedVatType] = useState(null);
  const [selectedProjectActivity, setSelectedProjectActivity] = useState(null);
  const [projectActivitySelectorOpen, setProjectActivitySelectorOpen] = useState(false);
  const [reportProject, setReportProject] = useState(null);
  const formikRef = useRef(null);
  const errorBannerRef = useRef(null);
  const [serverErrorMessages, setServerErrorMessages] = useState([]);

  /** Only derived from saved priceLine — never from selectedVatType — so enableReinitialize does not
   *  reset the whole form when VAT types load async (which broke auto calc + default selection). */
  const formInitialValues = useMemo(() => {
    const excl = priceLine?.agreed_price;
    const hasExcl = excl != null && excl !== "";
    const incl = priceLine?.agreed_price_incl_vat;
    const hasIncl = incl != null && incl !== "";
    const pctNested = priceLine?.vat_type?.percentage;

    let agreed_price_incl_vat = "0,00";
    if (hasIncl) {
      agreed_price_incl_vat = formatPrice(incl);
    } else if (hasExcl && Number.isFinite(Number(pctNested))) {
      agreed_price_incl_vat = formatPrice(calculateInclVat(excl, Number(pctNested)));
    } else if (hasExcl) {
      agreed_price_incl_vat = formatPrice(calculateInclVat(excl, DEFAULT_VAT_PERCENT_FALLBACK));
    }

    return {
      title: priceLine?.title || "",
      description: priceLine?.description || "",
      project_activity_id: priceLine?.project_activity_id || null,
      agreed_price: hasExcl ? formatPrice(excl) : "0,00",
      agreed_price_incl_vat,
      agreed_hours: priceLine?.agreed_hours || 0,
      is_agreed_price_line: priceLine?.is_agreed_price_line !== false,
      row_order: priceLine?.row_order || 0,
      vat_type_id: priceLine?.vat_type_id ?? null,
    };
  }, [
    priceLine?.id,
    priceLine?.title,
    priceLine?.description,
    priceLine?.project_activity_id,
    priceLine?.agreed_price,
    priceLine?.agreed_price_incl_vat,
    priceLine?.agreed_hours,
    priceLine?.is_agreed_price_line,
    priceLine?.row_order,
    priceLine?.vat_type_id,
    priceLine?.vat_type?.id,
    priceLine?.vat_type?.percentage,
  ]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        title: Yup.string().required(t("report.price_lines.title_required")),
        agreed_price: Yup.mixed().when("is_agreed_price_line", {
          is: (v) => v !== false,
          then: (schema) =>
            schema.test(
              "agreed-price-or-hours",
              t("report.price_lines.validation.agreed_price_or_hours"),
              function (value) {
                const parent = this.parent;
                const price = parsePrice(value);
                const rawH = parent.agreed_hours;
                const hours =
                  rawH === "" || rawH === null || rawH === undefined ? 0 : Number(rawH);
                const h = Number.isFinite(hours) ? hours : 0;
                return price > 0 || h > 0;
              }
            ),
          otherwise: (schema) => schema,
        }),
        agreed_hours: Yup.mixed().when("is_agreed_price_line", {
          is: (v) => v !== false,
          then: (schema) =>
            schema.test(
              "agreed-price-or-hours-h",
              t("report.price_lines.validation.agreed_price_or_hours"),
              function (value) {
                const parent = this.parent;
                const price = parsePrice(parent.agreed_price);
                const rawH = value;
                const hours =
                  rawH === "" || rawH === null || rawH === undefined ? 0 : Number(rawH);
                const h = Number.isFinite(hours) ? hours : 0;
                return price > 0 || h > 0;
              }
            ),
          otherwise: (schema) => schema,
        }),
      }),
    [t]
  );

  useEffect(() => {
    if (open) {
      setServerErrorMessages([]);
    }
  }, [open]);

  useEffect(() => {
    if (serverErrorMessages.length > 0 && errorBannerRef.current) {
      errorBannerRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [serverErrorMessages]);

  useEffect(() => {
    if (open && reportId) {
      if (!project) {
        fetchReportProject();
      } else {
        setReportProject(project);
      }

      if (priceLine?.vat_type) {
        setSelectedVatType(priceLine.vat_type);
      } else {
        setSelectedVatType(null);
      }

      if (priceLine?.project_activity) {
        setSelectedProjectActivity(priceLine.project_activity);
      } else if (priceLine?.project_activity_id) {
        setSelectedProjectActivity({ id: priceLine.project_activity_id });
      } else {
        setSelectedProjectActivity(null);
      }

      let cancelled = false;
      (async () => {
        try {
          const response = await axios.get(`/vat_types`);
          if (cancelled) return;
          const raw = response.data;
          const types = Array.isArray(raw) ? raw : raw?.vat_types || [];

          if (!priceLine?.vat_type_id) {
            const defaultVatType = pickDefaultVatType(types);
            if (defaultVatType) setSelectedVatType(defaultVatType);
          } else if (priceLine?.vat_type_id && !priceLine.vat_type) {
            const existingType = types.find((vt) => vt.id === priceLine.vat_type_id);
            if (existingType) {
              setSelectedVatType(existingType);
            } else if (types.length > 0) {
              setSelectedVatType(pickDefaultVatType(types));
            }
          }
        } catch (err) {
          if (!cancelled) console.error("Error fetching vat types:", err);
        }
      })();

      return () => {
        cancelled = true;
      };
    }

    setSelectedVatType(null);
    setSelectedProjectActivity(null);
    setProjectActivitySelectorOpen(false);
    setReportProject(null);
    return undefined;
  }, [open, reportId, priceLine?.vat_type_id, priceLine?.vat_type, priceLine?.project_activity_id, priceLine?.project_activity, project]);
  
  const fetchReportProject = async () => {
    try {
      const response = await axios.get(`/service/reports/${reportId}`, {
        params: { fields: "project" }
      });
      setReportProject(response.data?.project || null);
    } catch (err) {
      console.error("Error fetching report project:", err);
      setReportProject(null);
    }
  };
  
  // Update formik when selectedVatType changes
  const handleVatTypeSelect = (vatType) => {
    setSelectedVatType(vatType);
  };
  
  // Recalculate incl. from excl. when VAT type changes; 25 % if ingen type / ugyldig sats
  useEffect(() => {
    if (!formikRef.current) return;
    if (selectedVatType) {
      formikRef.current.setFieldValue("vat_type_id", selectedVatType.id);
    }
    const pct = getVatPercentForCalculation(selectedVatType);
    const agreedPrice = formikRef.current.values.agreed_price;
    if (agreedPrice != null && agreedPrice !== "") {
      const exclVat = parsePrice(agreedPrice);
      const inclVat = calculateInclVat(exclVat, pct);
      formikRef.current.setFieldValue("agreed_price_incl_vat", formatPrice(inclVat));
    }
  }, [selectedVatType]);

  const handleSubmit = async (values, helpers) => {
    setServerErrorMessages([]);
    helpers.setStatus(null);
    try {
      const agreedHoursRaw = values.agreed_hours;
      const agreedHoursNum =
        agreedHoursRaw === "" || agreedHoursRaw === null || agreedHoursRaw === undefined
          ? 0
          : Number(agreedHoursRaw);
      const agreedHours = Number.isFinite(agreedHoursNum) ? agreedHoursNum : 0;

      // Prepare price line data
      const priceLineData = {
        report_price_line: {
          title: values.title || "",
          description: values.description || "",
          project_activity_id: values.project_activity_id || null,
          activity_item_id: null,
          agreed_price: parsePrice(values.agreed_price) || 0,
          agreed_price_incl_vat: parsePrice(values.agreed_price_incl_vat) || 0,
          agreed_hours: agreedHours,
          is_agreed_price_line: values.is_agreed_price_line !== false,
          row_order: values.row_order || 0,
          vat_type_id: values.vat_type_id || (selectedVatType?.id || null),
        }
      };

      let response;
      if (isEditing) {
        // Update existing price line
        response = await axios.put(`/service/reports/${reportId}/price_lines/${priceLine.id}`, priceLineData);
      } else {
        // Create new price line
        response = await axios.post(`/service/reports/${reportId}/price_lines`, priceLineData);
      }

      if (response.status === 200 || response.status === 201) {
        const savedPriceLine = response.data;

        if (onSuccess) {
          onSuccess(savedPriceLine);
        }
        toast.success(isEditing ? t("report.price_lines.updated") : t("report.price_lines.created"));
        helpers.setSubmitting(false);
      }
    } catch (err) {
      console.error("Error saving report price line:", err);
      const data = err.response?.data;
      const messages = extractApiErrorMessages(data);
      const displayMessages =
        messages.length > 0 ? messages : err.message ? [err.message] : [t("report.price_lines.save_error")];
      setServerErrorMessages(displayMessages);

      if (formikRef.current && data?.errors && typeof data.errors === "object" && !Array.isArray(data.errors)) {
        const fieldErrors = {};
        Object.entries(data.errors).forEach(([key, val]) => {
          const arr = Array.isArray(val) ? val : [val];
          const text = arr.filter(Boolean).join(" ");
          if (text) fieldErrors[key] = text;
        });
        if (Object.keys(fieldErrors).length > 0) {
          formikRef.current.setErrors({ ...formikRef.current.errors, ...fieldErrors });
        }
      }

      const detail = displayMessages.join(" ");
      toast.error(`${t("report.price_lines.save_error")}: ${detail}`);
      helpers.setSubmitting(false);
      helpers.setErrors({});
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={isEditing ? t("report.price_lines.edit") : t("report.price_lines.add")}
    >
      <div className="mt-4">
        <Formik
          initialValues={formInitialValues}
          enableReinitialize={true}
          validateOnChange={true}
          validateOnBlur={true}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {(formik) => {
            // Store formik reference
            formikRef.current = formik;
            
            return (
            <Form>
              <div className="grid grid-cols-6 gap-6">
                {serverErrorMessages.length > 0 && (
                  <div
                    ref={errorBannerRef}
                    className="col-span-6 rounded-md bg-red-50 p-4 text-sm text-red-900 border-2 border-red-300 shadow-sm"
                    role="alert"
                    aria-live="assertive"
                  >
                    <p className="font-semibold mb-2">{t("report.price_lines.server_errors_title")}</p>
                    <ul className="list-disc space-y-1.5 pl-5 mb-2">
                      {serverErrorMessages.map((msg, i) => (
                        <li key={i} className="break-words">
                          {msg}
                        </li>
                      ))}
                    </ul>
                    <p className="text-red-800/90">{t("report.price_lines.field_errors_hint")}</p>
                  </div>
                )}
                {formik.submitCount > 0 && !formik.isValid && serverErrorMessages.length === 0 && (
                  <div
                    className="col-span-6 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900"
                    role="status"
                  >
                    {t("report.price_lines.field_errors_hint")}
                  </div>
                )}
                <div className="col-span-6 sm:col-span-6">
                  <PMField
                    name="title"
                    type="text"
                    label={t("report.price_lines.title_field")}
                    {...formik}
                  />
                </div>

                <div className="col-span-6 sm:col-span-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("report.price_lines.project_activity")}
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setProjectActivitySelectorOpen(true);
                      formik.setFieldTouched("project_activity_id", true);
                    }}
                    disabled={!(project || reportProject)?.id}
                    className={`w-full px-3 py-2 text-left border rounded-md shadow-sm bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 hover:bg-gray-50 ${
                      formik.errors.project_activity_id && (formik.submitCount > 0 || formik.touched.project_activity_id)
                        ? "border-red-500 ring-1 ring-red-500"
                        : "border-gray-300"
                    } ${!(project || reportProject)?.id ? "opacity-50 cursor-not-allowed" : ""}`}
                    title={!(project || reportProject)?.id ? t("report.price_lines.select_project_first") : ""}
                  >
                    {selectedProjectActivity?.display_title || selectedProjectActivity?.title ? (
                      <div
                        className="truncate"
                        title={
                          selectedProjectActivity.display_title || selectedProjectActivity.title
                        }
                      >
                        {selectedProjectActivity.display_title ||
                          selectedProjectActivity.title ||
                          `ID ${selectedProjectActivity.id}`}
                      </div>
                    ) : (
                      <span className="text-gray-500">{t("report.price_lines.select_project_activity")}</span>
                    )}
                  </button>
                  {selectedProjectActivity && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedProjectActivity(null);
                        formik.setFieldValue("project_activity_id", null);
                        formik.setFieldError("project_activity_id", undefined);
                      }}
                      className="mt-1 text-sm text-red-600 hover:text-red-800"
                    >
                      {t("report.price_lines.remove_activity")}
                    </button>
                  )}
                  {formik.errors.project_activity_id &&
                    (formik.submitCount > 0 || formik.touched.project_activity_id) && (
                      <p className="mt-1 text-sm text-red-600">{formik.errors.project_activity_id}</p>
                    )}
                </div>

                <div className="col-span-6 sm:col-span-6">
                  <PMField
                    name="description"
                    type="text"
                    multiline
                    rows={3}
                    label={t("report.price_lines.description_field")}
                    {...formik}
                  />
                </div>

                <div className="col-span-6 sm:col-span-3">
                  <PMSwitch
                    name="is_agreed_price_line"
                    label={t("report.price_lines.is_agreed_price")}
                    checked={formik.values.is_agreed_price_line !== false}
                    onChange={(e) => {
                      formik.setFieldValue("is_agreed_price_line", e.target.checked);
                      formik.setFieldError("agreed_price", undefined);
                      formik.setFieldError("agreed_hours", undefined);
                    }}
                    disabled={false}
                  />
                </div>

                {formik.values.is_agreed_price_line !== false && (
                  <>
                    <div className="col-span-6 sm:col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("report.price_lines.vat_type")}
                      </label>
                      <VatTypeSelectorButton
                        selectedVatType={selectedVatType}
                        onSelect={handleVatTypeSelect}
                        placeholder={t("report.price_lines.select_vat_type")}
                      />
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("report.price_lines.agreed_price_excl_vat")}
                      </label>
                      <input
                        type="text"
                        name="agreed_price"
                        value={formik.values.agreed_price || "0,00"}
                        onChange={(e) => {
                          let value = e.target.value;
                          // Replace dot with comma
                          value = value.replace(".", ",");
                          // Only allow numbers, comma, and one comma
                          value = value.replace(/[^0-9,]/g, "");
                          // Ensure only one comma
                          const parts = value.split(",");
                          if (parts.length > 2) {
                            value = parts[0] + "," + parts.slice(1).join("");
                          }
                          
                          formik.setFieldValue("agreed_price", value);

                          const exclVat = parsePrice(value);
                          const vatPercentage = getVatPercentForCalculation(selectedVatType);
                          const inclVat = calculateInclVat(exclVat, vatPercentage);
                          formik.setFieldValue("agreed_price_incl_vat", formatPrice(inclVat));
                        }}
                        onBlur={(e) => {
                          const value = parsePrice(e.target.value);
                          formik.setFieldValue("agreed_price", formatPrice(value));
                          const pct = getVatPercentForCalculation(selectedVatType);
                          formik.setFieldValue(
                            "agreed_price_incl_vat",
                            formatPrice(calculateInclVat(value, pct))
                          );
                          formik.setFieldTouched("agreed_price", true);
                        }}
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                          (formik.submitCount > 0 || formik.touched.agreed_price) &&
                          (formik.errors.agreed_price || formik.errors.agreed_hours)
                            ? "border-red-500 ring-1 ring-red-500"
                            : "border-gray-300"
                        }`}
                        placeholder="0,00"
                        aria-invalid={
                          !!(formik.errors.agreed_price || formik.errors.agreed_hours) &&
                          (formik.submitCount > 0 || formik.touched.agreed_price)
                        }
                      />
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("report.price_lines.agreed_price_incl_vat")}
                      </label>
                      <input
                        type="text"
                        name="agreed_price_incl_vat"
                        value={formik.values.agreed_price_incl_vat || "0,00"}
                        onChange={(e) => {
                          let value = e.target.value;
                          // Replace dot with comma
                          value = value.replace(".", ",");
                          // Only allow numbers, comma, and one comma
                          value = value.replace(/[^0-9,]/g, "");
                          // Ensure only one comma
                          const parts = value.split(",");
                          if (parts.length > 2) {
                            value = parts[0] + "," + parts.slice(1).join("");
                          }
                          
                          formik.setFieldValue("agreed_price_incl_vat", value);

                          const inclVat = parsePrice(value);
                          const vatPercentage = getVatPercentForCalculation(selectedVatType);
                          const exclVat = calculateExclVat(inclVat, vatPercentage);
                          formik.setFieldValue("agreed_price", formatPrice(exclVat));
                        }}
                        onBlur={(e) => {
                          const value = parsePrice(e.target.value);
                          formik.setFieldValue("agreed_price_incl_vat", formatPrice(value));
                          const pct = getVatPercentForCalculation(selectedVatType);
                          formik.setFieldValue(
                            "agreed_price",
                            formatPrice(calculateExclVat(value, pct))
                          );
                          formik.setFieldTouched("agreed_price_incl_vat", true);
                        }}
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                          (formik.submitCount > 0 || formik.touched.agreed_price_incl_vat) &&
                          (formik.errors.agreed_price || formik.errors.agreed_hours)
                            ? "border-red-500 ring-1 ring-red-500"
                            : "border-gray-300"
                        }`}
                        placeholder="0,00"
                        aria-invalid={
                          !!(formik.errors.agreed_price || formik.errors.agreed_hours) &&
                          (formik.submitCount > 0 || formik.touched.agreed_price_incl_vat)
                        }
                      />
                    </div>

                    {(formik.submitCount > 0 ||
                      formik.touched.agreed_price ||
                      formik.touched.agreed_price_incl_vat ||
                      formik.touched.agreed_hours) &&
                      (formik.errors.agreed_price || formik.errors.agreed_hours) && (
                        <div className="col-span-6 text-sm text-red-600" role="alert">
                          {formik.errors.agreed_price || formik.errors.agreed_hours}
                        </div>
                      )}

                    <div className="col-span-6 sm:col-span-3">
                      <div
                        className={
                          (formik.submitCount > 0 || formik.touched.agreed_hours) &&
                          (formik.errors.agreed_price || formik.errors.agreed_hours)
                            ? "rounded-md ring-1 ring-red-500 p-0.5 -m-0.5"
                            : ""
                        }
                      >
                        <PMField
                          name="agreed_hours"
                          type="number"
                          step="0.5"
                          label={t("report.price_lines.agreed_hours")}
                          {...formik}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                <button
                  type="button"
                  className="inline-flex justify-center w-20 rounded-md border border-transparent bg-gray-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                  onClick={onClose}
                >
                  {t("Cancel")}
                </button>

                <button
                  type="submit"
                  disabled={formik.isSubmitting}
                  className="inline-flex align-right justify-center rounded-md border border-transparent bg-emerald-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm disabled:bg-gray-400"
                >
                  {formik.isSubmitting
                    ? isEditing
                      ? t("report.price_lines.updating")
                      : t("report.price_lines.adding")
                    : isEditing
                    ? t("Update")
                    : t("report.price_lines.add")}
                </button>
              </div>
            </Form>
            );
          }}
        </Formik>
      </div>

      {(project || reportProject)?.id && (
        <div className={`fixed inset-0 z-50 ${projectActivitySelectorOpen ? "block" : "hidden"}`}>
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => setProjectActivitySelectorOpen(false)}
          />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
              <ModernProjectActivitySelector
                open={projectActivitySelectorOpen}
                project={project || reportProject}
                selectedProjectActivities={selectedProjectActivity ? [selectedProjectActivity] : []}
                onChange={(list) => {
                  if (list.length > 0) {
                    const pa = list[0];
                    setSelectedProjectActivity(pa);
                    if (formikRef.current) {
                      formikRef.current.setFieldValue("project_activity_id", pa.id);
                      formikRef.current.setFieldError("project_activity_id", undefined);
                    }
                  } else {
                    setSelectedProjectActivity(null);
                    if (formikRef.current) {
                      formikRef.current.setFieldValue("project_activity_id", null);
                      formikRef.current.setFieldError("project_activity_id", undefined);
                    }
                  }
                }}
                onClose={() => setProjectActivitySelectorOpen(false)}
                multiple={false}
                showHeader={true}
              />
            </div>
          </div>
        </div>
      )}
    </PMDialog>
  );
};

